<!--
  The patient-facing form page — tasks/patient-forms.md §5 host 2.

  ONE COLUMN AT EVERY WIDTH. This is a phone held one-handed in a waiting room; the desktop case is
  the same layout with more margin. Nothing here scrolls sideways and nothing is behind a hover.

  Every field on the page is drawn by <app-dynamic-form-renderer>. The markup below is the shell,
  the paging chrome and the five screens — and that is deliberate: a page that drew its own inputs
  would be a second implementation of the schema (§5).
-->

<div class="pf-page">

  <!-- ══ Practice branding ═══════════════════════════════════════════════════════════════════
       Shown on EVERY screen, including the refusals. A form that looks nothing like the practice
       that sent it reads as phishing, and a patient deciding whether to trust an SMS link decides
       it here. `Group` carries no logo column anywhere in this tree, so the practice's name and
       its contact details are the whole of the branding — the same set the invite wizard uses.
  -->
  <header class="pf-brand" *ngIf="practice">
    <h1 class="pf-brand-name">{{ practiceName }}</h1>
    <p class="pf-brand-about" *ngIf="practiceAbout">{{ practiceAbout }}</p>
    <div class="pf-brand-contact" *ngIf="hasPracticeContact">
      <span *ngIf="practiceNumber">Practice no. {{ practiceNumber }}</span>
      <a *ngIf="practicePhone" [href]="'tel:' + practicePhone">{{ practicePhone }}</a>
      <a *ngIf="practiceEmail" [href]="'mailto:' + practiceEmail">{{ practiceEmail }}</a>
      <span *ngIf="practiceOpeningTimes">{{ practiceOpeningTimes }}</span>
    </div>
  </header>

  <!-- ══ Loading ════════════════════════════════════════════════════════════════════════════ -->
  <section class="pf-card pf-centred" *ngIf="phase === 'loading'" data-testid="pf-loading">
    <mat-progress-spinner mode="indeterminate" diameter="44"></mat-progress-spinner>
    <p class="pf-muted">Opening your form…</p>
  </section>

  <!-- ══ The four refusals ══════════════════════════════════════════════════════════════════
       ONE BLOCK, FOUR SCREENS. `data-testid` carries the state so each is separately assertable,
       and the heading and body differ for every one of them: `completed` congratulates, `expired`
       and `revoked` send the patient to the practice for a new link, `not_found` asks them to
       check the link first. None of them offers a retry, because none of them can succeed.
  -->
  <section class="pf-card pf-centred pf-blocked" *ngIf="phase === 'blocked'"
    [attr.data-testid]="'pf-blocked-' + blockedState">
    <mat-icon class="pf-blocked-icon" [class.pf-blocked-ok]="blockedState === 'completed'">
      {{ blockedIcon }}
    </mat-icon>
    <h2 class="pf-blocked-heading">{{ blockedHeading }}</h2>
    <p class="pf-blocked-message">{{ blockedMessage }}</p>
  </section>

  <!-- ══ An unexpected failure — the ONLY screen with a retry ═══════════════════════════════ -->
  <section class="pf-card pf-centred" *ngIf="phase === 'error'" data-testid="pf-error">
    <mat-icon class="pf-blocked-icon">cloud_off</mat-icon>
    <h2 class="pf-blocked-heading">Something went wrong</h2>
    <p class="pf-blocked-message">{{ loadErrorMessage }}</p>
    <button mat-flat-button color="primary" type="button" class="pf-action"
      (click)="beginLoad()" data-testid="pf-retry">
      Try again
    </button>
  </section>

  <!-- ══ Completion ═════════════════════════════════════════════════════════════════════════
       §11 decision 3: there is NO emailed copy, so this screen is the only confirmation the
       patient will ever get. It has to be unambiguous that they are finished and that nothing
       further is expected of them — hence the second line, which is not decoration.
  -->
  <section class="pf-card pf-centred pf-done" *ngIf="phase === 'done'" data-testid="pf-completed">
    <mat-icon class="pf-blocked-icon pf-blocked-ok">check_circle</mat-icon>
    <h2 class="pf-blocked-heading">Thank you — your form has been sent</h2>
    <p class="pf-blocked-message">{{ completedMessage }}</p>
    <p class="pf-muted">
      There is nothing further you need to do. You can close this page.
    </p>
  </section>

  <!-- ══ The form ═══════════════════════════════════════════════════════════════════════════ -->
  <ng-container *ngIf="phase === 'form'">

    <section class="pf-card pf-intro">
      <h2 class="pf-form-title">{{ formTitle }}</h2>
      <p class="pf-form-description" *ngIf="formDescription">{{ formDescription }}</p>
      <p class="pf-muted" *ngIf="expiresAt">
        Please complete this form by {{ expiresAt | date: 'd MMMM y' }}.
      </p>
    </section>

    <!-- Progress. A step counter alone is meaningless on a ten-section form; the bar is what
         tells the patient whether they are three minutes in or thirty. -->
    <div class="pf-progress" *ngIf="sections.length > 1" data-testid="pf-progress">
      <div class="pf-progress-label">
        <span>Section {{ currentSectionIndex + 1 }} of {{ sections.length }}</span>
        <span class="pf-muted" *ngIf="currentSection">{{ currentSection.title }}</span>
      </div>
      <mat-progress-bar mode="determinate" [value]="progressPercent"></mat-progress-bar>
    </div>

    <!-- The server's per-question complaints, addressed to the questions they belong to.
         The server validates against the PINNED version and is the authority — this list is its
         words, not a client re-derivation. -->
    <div class="pf-card pf-server-errors" *ngIf="serverErrors.length > 0"
      data-testid="pf-server-errors" role="alert">
      <p class="pf-server-errors-lead">{{ submitError }}</p>
      <ul>
        <li *ngFor="let error of serverErrors; trackBy: trackError">
          <button type="button" class="pf-error-link" (click)="goToError(error.questionId)">
            {{ questionLabel(error.questionId) }}
          </button>
          <span class="pf-error-text"> — {{ error.message }}</span>
        </li>
      </ul>
    </div>

    <section class="pf-card pf-form">
      <app-dynamic-form-renderer
        [schema]="schema"
        [value]="answers"
        [sectionId]="currentSectionId"
        (valueChange)="onAnswersChange($event)"
        (sectionsChange)="onSectionsChange($event)">
      </app-dynamic-form-renderer>
    </section>

    <p class="pf-step-error" *ngIf="stepError" role="alert" data-testid="pf-step-error">
      {{ stepError }}
    </p>

    <p class="pf-submit-error" *ngIf="submitError && serverErrors.length === 0" role="alert"
      data-testid="pf-submit-error">
      {{ submitError }} Your answers are still here — nothing has been lost.
    </p>

    <!-- Navigation. 44px targets, full width on a phone, Next and Back never adjacent-and-equal
         so a thumb cannot mistake one for the other. -->
    <nav class="pf-nav">
      <button mat-stroked-button type="button" class="pf-action pf-back" *ngIf="!isFirstSection"
        (click)="back()" data-testid="pf-back">
        Back
      </button>
      <button mat-flat-button color="primary" type="button" class="pf-action pf-next"
        *ngIf="!isLastSection" (click)="next()" data-testid="pf-next">
        Next
      </button>
      <button mat-flat-button color="primary" type="button" class="pf-action pf-submit"
        *ngIf="isLastSection" [disabled]="isSubmitting" (click)="submit()" data-testid="pf-submit">
        {{ isSubmitting ? 'Sending…' : 'Submit form' }}
      </button>
    </nav>

    <!-- Autosave, shown quietly but shown. A patient who does not know their progress is kept
         will not close the tab and come back on a laptop — which is the entire reason the draft
         lives on the server rather than in localStorage. -->
    <p class="pf-save" aria-live="polite" data-testid="pf-save-state">
      <ng-container [ngSwitch]="saveState">
        <span *ngSwitchCase="'pending'" class="pf-muted">Saving your progress…</span>
        <span *ngSwitchCase="'saving'" class="pf-muted">Saving your progress…</span>
        <span *ngSwitchCase="'saved'" class="pf-saved">
          <mat-icon inline="true">cloud_done</mat-icon>
          Progress saved. You can close this page and come back to this link later.
        </span>
        <span *ngSwitchCase="'failed'" class="pf-save-failed">
          We could not save your progress just yet — keep going, we will try again.
        </span>
        <span *ngSwitchDefault class="pf-muted">
          Your progress is saved automatically as you go.
        </span>
      </ng-container>
    </p>

  </ng-container>

  <!-- Shown under every screen: the practice is the only support path a patient has, and there is
       deliberately no way to request a new link from here (§14.3 decision 2 — a resend rotates the
       token, so any link this page could offer would already be dead). -->
  <footer class="pf-footer" *ngIf="hasPracticeContact">
    <p class="pf-muted">
      Need help with this form? Please contact {{ practiceName }}<a *ngIf="practicePhone"
        [href]="'tel:' + practicePhone"> on {{ practicePhone }}</a>.
    </p>
  </footer>

</div>
